import {
    useCallback,
    useState,
    useEffect,
    useRef,
    FC,
    createElement,
    Dispatch,
    SetStateAction,
    MutableRefObject
} from "react"
import { useDispatch } from "react-redux"
import type { JSONSchema7 } from "json-schema"
import Form, { ErrorSchema } from "@rjsf/core"
import { SchemaForm as SchemaFormType } from "universal-schema/lib"
import { SchemaFormRefProps } from "resha"

import { setVisualOnboarding } from "../../../../Actions/Client/InputRender/InputRenderActions"
import { updateJobOutput } from "../../../../Actions/API/Job/JobActions"
import { useUrlSignerFromContext } from "../../../../Hooks/useUrlSigner"
import { useBoolean } from "../../../../Hooks/useBoolean"
import { getValue, hasValue } from "../../../../Models/Client/FetchState"
import { InputFormRenderProps } from "../../InputFormRender/InputFormRender"
import {
    DocumentsExtractionContext,
    useDocumentsExtractionState
} from "./DocumentsExtractionContext"

export type InputFormData = {
    signedUrl: string
    documentUrl: string
}

type DocumentsExtractionProps<T> = {
    appID?: string
    submitting: boolean
    onBack?: () => void
    onForward?: () => void
    input?: InputFormRenderProps & React.RefAttributes<SchemaFormRefProps>
    output: Omit<InputFormRenderProps, "schema" | "uiSchema"> &
        React.RefAttributes<SchemaFormRefProps>
    parameterSchema?: SchemaFormType
    demo?: {
        active: boolean
        toggle: () => void
    }
    children: FC<DocumentsExtractionChildProps<T>>
} & T

export type DocumentsExtractionChildProps<T> = {
    submitting: boolean
    onChange: (value: unknown) => void
    onSubmit: () => void
    onBack?: () => void
    onForward?: () => void
    errors?: ErrorSchema
    visualEditor: [boolean, () => void, () => void]
    selectedDocument: [
        string | undefined,
        Dispatch<SetStateAction<string | undefined>>
    ]
    input?: InputFormRenderProps & React.RefAttributes<SchemaFormRefProps>
    output: Omit<InputFormRenderProps, "schema" | "uiSchema"> &
        React.RefAttributes<SchemaFormRefProps>
    parameterSchema: SchemaFormType | undefined
    demo?: {
        active: boolean
        toggle: () => void
    }
} & T

export function DocumentsExtraction<T>({
    appID,
    submitting,
    onForward,
    onBack,
    input,
    output,
    parameterSchema,
    demo,
    children,
    ...props
}: DocumentsExtractionProps<T>) {
    const urlSigner = useUrlSignerFromContext()

    const dispatch = useDispatch()

    const formRef = useRef<Form<unknown>>(null)

    const [errors, setErrors] = useState<ErrorSchema>()

    const [signedUrl, setSignedUrl] = useState<string>()

    const selectedDocument = useState<string | undefined>(
        (input?.formData as InputFormData)?.documentUrl
    )
    const [documentUrl, setSelectedDocument] = selectedDocument

    const [isVisualEditor, openVisualEditor, closeVisualEditor] =
        useBoolean(false)

    const context = useDocumentsExtractionState(
        appID,
        parameterSchema?.schema,
        output.formData,
        isVisualEditor
    )

    useEffect(() => {
        if (documentUrl) {
            urlSigner(documentUrl).then(url => {
                setSignedUrl(url)
            })
        }
    }, [documentUrl, urlSigner])

    const handleOpenVisualEditor = () => {
        openVisualEditor()
        dispatch(setVisualOnboarding(true))
    }

    const handleCloseVisualEditor = () => {
        closeVisualEditor()
        dispatch(setVisualOnboarding(false))
    }

    const handleSubmit = useCallback(async () => {
        if (parameterSchema?.schema) {
            const validationResult = formRef.current?.validate(
                output.formData,
                parameterSchema?.schema
            )

            if (validationResult?.errors.length) {
                setErrors(validationResult.errorSchema)
                output.onError?.(validationResult.errors)
            } else {
                const { appSchema, annotationsOutputList, selectedJob } =
                    context
                if (
                    isVisualEditor &&
                    appID &&
                    selectedJob &&
                    hasValue(appSchema)
                ) {
                    await dispatch(
                        updateJobOutput(
                            appID,
                            selectedJob.uuid,
                            selectedJob.id,
                            annotationsOutputList,
                            getValue(appSchema)?.outputSchema as JSONSchema7,
                            `jobs/${selectedJob.id}/output`,
                            true,
                            false,
                            false
                        )
                    )
                }
                output.onSubmit?.(output.formData)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [output.formData, parameterSchema?.schema, formRef.current])

    const handleChange = (value: unknown) => {
        output.onChange?.(value, [])
        setErrors(undefined)
    }

    const handleDocumentChange = (
        value: SetStateAction<string | undefined>
    ) => {
        setSignedUrl(undefined)
        setSelectedDocument(value)
    }

    useEffect(() => {
        if (output.ref) {
            ;(output.ref as MutableRefObject<{}>).current = {
                submit: handleSubmit
            }
        }
    }, [output.ref, handleSubmit])

    return (
        <DocumentsExtractionContext.Provider value={context}>
            {parameterSchema !== undefined && ( // hidden empty schema form to operate any schema via ref callbacks
                <Form ref={formRef} schema={{}} className="hidden" />
            )}
            {createElement(children, {
                appID,
                input: {
                    ...input,
                    formData: {
                        ...((input?.formData as InputFormData) || {}),
                        signedUrl
                    }
                },
                output,
                parameterSchema,
                errors,
                demo,
                visualEditor: [
                    isVisualEditor,
                    handleOpenVisualEditor,
                    handleCloseVisualEditor
                ],
                selectedDocument: [documentUrl, handleDocumentChange],
                submitting,
                onChange: handleChange,
                onSubmit: handleSubmit,
                onForward,
                onBack,
                ...props
            } as unknown as DocumentsExtractionChildProps<T>)}
        </DocumentsExtractionContext.Provider>
    )
}
